import { z } from "zod";
import type { Date as PDate } from "../../generated/core/v1/date";
import { Lender, LoanType } from "../../generated/core/v1/debts";
import type { Family } from "../../generated/core/v1/family";
import type { Person } from "../../generated/core/v1/person";
import type { Questionnaire } from "../../generated/core/v1/questionnaire";
import { addYears, ageOn, toIso } from "../lib/dates";
import { parseNumber } from "../lib/format";
import { newUuid } from "../lib/uuid";
import {
  carChanges,
  coverageInForce,
  expenseChanges,
  financedChanges,
  fuelMonthly,
  homeChanges,
  type InsuranceInput,
  insuranceChanges,
  jobChanges,
  type PeriodType,
} from "./changes";

/**
 * The Decisions tools' form values: what each form holds (strings as typed, so a half-typed amount
 * never jumps around), the rules a run needs them to meet, the values a new form starts from, and
 * the run itself (values to Questionnaire changes). The forms and AbiChat's simulation tools both
 * use these, so a what-if from either is the same scenario, saved with the same `inputs`, and opens
 * in the same form.
 */

// ------------------------------------------------------------------ zod helpers

const num = (v: string) => parseNumber(v);

export const zAmount = (msg = "Enter an amount") =>
  z.string().refine((v) => {
    const n = num(v);
    return n !== undefined && n >= 0;
  }, msg);

export const zPositive = (msg = "Enter an amount above zero") =>
  z.string().refine((v) => {
    const n = num(v);
    return n !== undefined && n > 0;
  }, msg);

export const zOptionalAmount = (msg = "Enter an amount, or leave it blank") =>
  z.string().refine((v) => v.trim() === "" || (num(v) ?? -1) >= 0, msg);

export const zRate = (msg = "Enter a rate, like 6.5") =>
  z.string().refine((v) => {
    const n = num(v);
    return n !== undefined && n >= 0 && n < 100;
  }, msg);

export const zDate = (msg = "Pick a date") =>
  z.string().refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v), msg);

export const zOptionalDate = () =>
  z.string().refine((v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v), "Pick a date");

/** A typed amount as a number (blank = 0). */
export const amount = (v: string | undefined) => num(v ?? "") ?? 0;
/** A typed amount, or undefined when blank. */
export const optAmount = (v: string | undefined) =>
  v === undefined || v.trim() === "" ? undefined : num(v);

/** A run: the changes, the values saved as the scenario's `inputs`, and any comparison changes. */
export type ToolRun = {
  changes: Questionnaire;
  inputs: Record<string, unknown>;
  comparison?: Questionnaire;
};

// ------------------------------------------------------------------ add an expense

export const expenseSchema = z
  .object({
    name: z.string().trim().min(1, "Give it a name"),
    amount: zPositive(),
    dueDate: zDate("When is it first due?"),
    recurring: z.boolean(),
    periodType: z.enum(["weekly", "monthly", "yearly"]),
    period: z.string(),
    endDate: zOptionalDate(),
  })
  .refine((v) => !v.recurring || (amount(v.period) >= 1 && Number.isInteger(amount(v.period))), {
    path: ["period"],
    message: "How many weeks, months or years between payments?",
  });

export type ExpenseValues = z.infer<typeof expenseSchema>;

export const expenseDefaults = (today: PDate): ExpenseValues => ({
  name: "",
  amount: "",
  dueDate: toIso(today),
  recurring: true,
  periodType: "monthly" as PeriodType,
  period: "1",
  endDate: "",
});

export function expenseRun(v: ExpenseValues, today: PDate): ToolRun {
  return {
    changes: expenseChanges(
      {
        name: v.name,
        amount: amount(v.amount),
        dueDate: v.dueDate,
        recurring: v.recurring,
        periodType: v.periodType,
        period: amount(v.period) || 1,
        endDate: v.endDate || undefined,
      },
      today,
    ),
    inputs: v,
  };
}

// ------------------------------------------------------------------ buy a home

export const LENDERS: [Lender, string][] = [
  [Lender.UNSPECIFIED, "Not sure yet"],
  [Lender.RBC, "RBC"],
  [Lender.TD, "TD"],
  [Lender.SCOTIABANK, "Scotiabank"],
  [Lender.BMO, "BMO"],
  [Lender.CIBC, "CIBC"],
  [Lender.NATIONAL_BANK, "National Bank"],
  [Lender.OTHER, "Another lender"],
];

export const homeSchema = z
  .object({
    name: z.string().trim().min(1, "Give it a name"),
    price: zPositive("What does the home cost?"),
    downPayment: zAmount(),
    rate: zRate(),
    amortizationYears: z
      .string()
      .refine((v) => amount(v) >= 1 && amount(v) <= 40, "Between 1 and 40 years"),
    purchaseDate: zDate("When would you buy it?"),
    primaryResidence: z.boolean(),
    annualPropertyTax: zOptionalAmount(),
    annualInsurance: zOptionalAmount(),
    lender: z.string(),
  })
  .refine((v) => amount(v.downPayment) <= amount(v.price), {
    path: ["downPayment"],
    message: "The down payment can't be more than the price",
  });

export type HomeValues = z.infer<typeof homeSchema>;

export const homeDefaults = (today: PDate): HomeValues => ({
  name: "New home",
  price: "",
  downPayment: "",
  rate: "4.5",
  amortizationYears: "25",
  purchaseDate: toIso(today),
  primaryResidence: true,
  annualPropertyTax: "",
  annualInsurance: "",
  lender: String(Lender.UNSPECIFIED),
});

export function homeRun(v: HomeValues, today: PDate): ToolRun {
  return {
    changes: homeChanges(
      {
        name: v.name,
        price: amount(v.price),
        downPayment: amount(v.downPayment),
        rate: amount(v.rate),
        amortizationYears: amount(v.amortizationYears),
        purchaseDate: v.purchaseDate,
        primaryResidence: v.primaryResidence,
        annualPropertyTax: optAmount(v.annualPropertyTax),
        annualInsurance: optAmount(v.annualInsurance),
        lender: Number(v.lender) as Lender,
      },
      today,
    ),
    inputs: v,
  };
}

// ------------------------------------------------------------------ buy a car

export const carSchema = z
  .object({
    name: z.string().trim().min(1, "Give it a name"),
    price: zPositive("What does the car cost?"),
    downPayment: zAmount(),
    apr: zRate(),
    termMonths: z.string().refine((v) => amount(v) >= 1 && amount(v) <= 120, "1 to 120 months"),
    purchaseDate: zDate("When would you buy it?"),
    insuranceMonthly: zOptionalAmount(),
    maintenanceMonthly: zOptionalAmount(),
    consumption: zOptionalAmount(),
    distance: zOptionalAmount(),
    fuelPrice: zOptionalAmount(),
    how: z.enum(["finance", "cash"]),
  })
  .refine((v) => v.how === "cash" || amount(v.downPayment) <= amount(v.price), {
    path: ["downPayment"],
    message: "The down payment can't be more than the price",
  });

export type CarValues = z.infer<typeof carSchema>;

export const carDefaults = (today: PDate): CarValues => ({
  name: "New car",
  price: "",
  downPayment: "",
  apr: "6.99",
  termMonths: "60",
  purchaseDate: toIso(today),
  insuranceMonthly: "",
  maintenanceMonthly: "",
  consumption: "",
  distance: "",
  fuelPrice: "",
  how: "finance" as "finance" | "cash",
});

export function carRun(v: CarValues, today: PDate): ToolRun {
  return {
    changes: carChanges(
      {
        name: v.name,
        price: amount(v.price),
        // Paying cash is a down payment of the whole price, and no loan.
        downPayment: v.how === "cash" ? amount(v.price) : amount(v.downPayment),
        apr: amount(v.apr),
        termMonths: amount(v.termMonths),
        purchaseDate: v.purchaseDate,
        insuranceMonthly: optAmount(v.insuranceMonthly),
        maintenanceMonthly: optAmount(v.maintenanceMonthly),
        fuelMonthly:
          fuelMonthly(amount(v.consumption), amount(v.distance), amount(v.fuelPrice)) || undefined,
      },
      today,
    ),
    inputs: v,
  };
}

// ------------------------------------------------------------------ a financed purchase

export const LOAN_TYPES: [LoanType, string][] = [
  [LoanType.PERSONAL_LOAN, "Personal loan"],
  [LoanType.LINE_OF_CREDIT, "Line of credit"],
  [LoanType.BUY_NOW_PAY_LATER, "Buy now, pay later"],
  [LoanType.OTHER, "Other financing"],
];

export const financedSchema = z
  .object({
    name: z.string().trim().min(1, "What are you buying?"),
    total: zPositive("What does it cost?"),
    downPayment: zAmount(),
    apr: zRate(),
    termMonths: z.string().refine((v) => amount(v) >= 1 && amount(v) <= 360, "1 to 360 months"),
    purchaseDate: zDate("When would you buy it?"),
    loanType: z.string(),
  })
  .refine((v) => amount(v.downPayment) <= amount(v.total), {
    path: ["downPayment"],
    message: "The down payment can't be more than the price",
  });

export type FinancedValues = z.infer<typeof financedSchema>;

export const financedDefaults = (today: PDate): FinancedValues => ({
  name: "",
  total: "",
  downPayment: "",
  apr: "9.9",
  termMonths: "24",
  purchaseDate: toIso(today),
  loanType: String(LoanType.PERSONAL_LOAN),
});

export function financedRun(v: FinancedValues, today: PDate): ToolRun {
  return {
    changes: financedChanges(
      {
        name: v.name,
        total: amount(v.total),
        downPayment: amount(v.downPayment),
        apr: amount(v.apr),
        termMonths: amount(v.termMonths),
        purchaseDate: v.purchaseDate,
        loanType: Number(v.loanType) as LoanType,
      },
      today,
    ),
    inputs: v,
  };
}

// ------------------------------------------------------------------ income changes

const existingJobSchema = z
  .object({
    personUuid: z.string(),
    jobUuid: z.string(),
    action: z.enum(["keep", "change", "end"]),
    income: z.string(),
    endDate: zOptionalDate(),
  })
  .refine((j) => j.action !== "change" || amount(j.income) > 0, {
    path: ["income"],
    message: "What would the new salary be?",
  })
  .refine((j) => j.action !== "end" || j.endDate !== "", {
    path: ["endDate"],
    message: "When would it end?",
  });

const offerSchema = z.object({
  uuid: z.string(),
  personUuid: z.string().min(1, "Whose job is it?"),
  title: z.string().trim().min(1, "What's the role?"),
  employer: z.string(),
  income: z.string().refine((v) => amount(v) > 0, "What is the salary before tax?"),
  startDate: z.string().refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v), "When would it start?"),
  endDate: zOptionalDate(),
});

export const jobSchema = z
  .object({ existing: z.array(existingJobSchema), offers: z.array(offerSchema) })
  .refine((v) => v.offers.length > 0 || v.existing.some((j) => j.action !== "keep"), {
    path: ["offers"],
    message: "Add an offer, or change one of your jobs, to see the difference.",
  });

export type JobValues = z.infer<typeof jobSchema>;
export type JobOfferValues = JobValues["offers"][number];

/** A new offer row: for the household's first member, starting today. */
export const newOffer = (family: Family, today: PDate): JobOfferValues => ({
  uuid: newUuid(),
  personUuid: family.persons[0]?.uuid ?? "",
  title: "",
  employer: "",
  income: "",
  startDate: toIso(today),
  endDate: "",
});

/** Every job the household has today, kept (or as saved), and one empty offer unless saved. */
export function jobDefaults(family: Family, today: PDate, saved?: Partial<JobValues>): JobValues {
  return {
    existing: family.persons.flatMap((person) =>
      person.jobs.map((job) => {
        const s = saved?.existing?.find((x) => x.jobUuid === job.uuid);
        return {
          personUuid: person.uuid,
          jobUuid: job.uuid,
          action: s?.action ?? "keep",
          income: s?.income ?? "",
          endDate: s?.endDate ?? "",
        };
      }),
    ),
    offers: saved?.offers ?? [newOffer(family, today)],
  };
}

export function jobRun(v: JobValues, family: Family, today: PDate): ToolRun {
  return {
    changes: jobChanges(
      {
        existing: v.existing.map((j) => ({
          personUuid: j.personUuid,
          jobUuid: j.jobUuid,
          action: j.action,
          income: j.action === "change" ? amount(j.income) : undefined,
          endDate: j.endDate || undefined,
        })),
        offers: v.offers.map((o) => ({
          uuid: o.uuid,
          personUuid: o.personUuid,
          title: o.title,
          employer: o.employer,
          income: amount(o.income),
          startDate: o.startDate,
          endDate: o.endDate || undefined,
        })),
      },
      family,
      today,
    ),
    inputs: v,
  };
}

// ------------------------------------------------------------------ insurance

const insurancePersonSchema = z
  .object({
    personUuid: z.string(),
    lifeHas: z.boolean(),
    lifeAmount: zOptionalAmount(),
    lifePremium: zOptionalAmount(),
    ciHas: z.boolean(),
    ciAmount: zOptionalAmount(),
    ciPremium: zOptionalAmount(),
    smoker: z.boolean(),
  })
  .refine((r) => !r.lifeHas || amount(r.lifeAmount) > 0, {
    path: ["lifeAmount"],
    message: "How much is it for?",
  })
  .refine((r) => !r.ciHas || amount(r.ciAmount) > 0, {
    path: ["ciAmount"],
    message: "How much is it for?",
  });

export const insuranceSchema = z
  .object({
    people: z.array(insurancePersonSchema),
    personUuid: z.string().min(1, "Who is it about?"),
    event: z.enum(["death", "critical_illness"]),
    date: zDate("When would it happen?"),
    durationYears: z.string(),
    coverage: zOptionalAmount(),
    extraPremium: zOptionalAmount(),
  })
  .refine((v) => v.event !== "critical_illness" || amount(v.durationYears) > 0, {
    path: ["durationYears"],
    message: "How long would they be off work?",
  });

export type InsuranceValues = z.infer<typeof insuranceSchema>;
export type InsurancePersonValues = InsuranceValues["people"][number];

/** A person's row before anything is said: no coverage held, not a smoker. */
export const insurancePersonDefaults = (personUuid: string): InsurancePersonValues => ({
  personUuid,
  lifeHas: false,
  lifeAmount: "",
  lifePremium: "",
  ciHas: false,
  ciAmount: "",
  ciPremium: "",
  smoker: false,
});

/**
 * When the event is set, by default (v41, Shayan's rule; a client-side default for the what-if's
 * date, not a financial rule): age 40 for anyone 35 or under, otherwise five years from now. Without
 * a birth date, five years from now.
 */
export function defaultEventDate(person: Person | undefined, today: PDate): PDate {
  const age = person?.dob ? ageOn(person.dob, today) : undefined;
  if (person?.dob && age !== undefined && age <= 35) return addYears(person.dob, 40);
  return addYears(today, 5);
}

/**
 * A new analysis: every person with no coverage held (v43: nobody is assumed to hold any), about the
 * first member with a job (else the first member), a death on the default date, two years off work
 * for an illness, and the coverage held tested. `saved` rows (by person) replace the blank ones.
 */
export function insuranceDefaults(
  family: Family,
  today: PDate,
  saved: InsurancePersonValues[] = [],
): InsuranceValues {
  const people = family.persons;
  const first = people.find((p) => p.jobs.length > 0) ?? people[0];
  const rows = new Map(saved.map((r) => [r.personUuid, r]));
  return {
    people: people.map((p) => rows.get(p.uuid) ?? insurancePersonDefaults(p.uuid)),
    personUuid: first?.uuid ?? "",
    event: "death",
    date: toIso(defaultEventDate(first, today)),
    durationYears: "2",
    coverage: "",
    extraPremium: "",
  };
}

/** What the insured person holds now for the event's product, from step one. */
export function heldFor(v: Pick<InsuranceValues, "people" | "personUuid" | "event">): number {
  const r = v.people?.find((p) => p.personUuid === v.personUuid);
  if (!r) return 0;
  return v.event === "death"
    ? r.lifeHas
      ? amount(r.lifeAmount)
      : 0
    : r.ciHas
      ? amount(r.ciAmount)
      : 0;
}

export function toInsuranceInput(v: InsuranceValues): InsuranceInput {
  return {
    event: v.event,
    personUuid: v.personUuid,
    date: v.date,
    durationYears: amount(v.durationYears),
    coverage: optAmount(v.coverage),
    held: heldFor(v),
    extraPremiumMonthly: optAmount(v.extraPremium),
  };
}

/**
 * The what-if's two runs (v44): the event with the coverage tested (`changes`) and the same event
 * on the coverage held now (`comparison`, without the extra coverage's premium). When the tested
 * amount is what's held, there's one run and no comparison. `inputs` carries `eventLines`, which
 * marks a scenario whose answer is drawn as event lines (older ones compared with today's plan).
 */
export function insuranceRun(v: InsuranceValues, family: Family, today: PDate): ToolRun {
  const input = toInsuranceInput(v);
  const held = Math.max(0, input.held ?? 0);
  return {
    changes: insuranceChanges(input, family, today),
    inputs: { ...v, eventLines: true },
    comparison:
      coverageInForce(input) === held
        ? undefined
        : insuranceChanges(
            { ...input, coverage: held, extraPremiumMonthly: undefined },
            family,
            today,
          ),
  };
}
